import { useEffect, useRef, useState } from 'react';
import '../../styles/components/subPageContentHolder/taraMCPConnections.scss';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { arrowHeadLeftSVG, rocketSVG } from '../../lib/allCustomSVGs';
import { useNavigate } from 'react-router-dom';
import get_all_connected_mcp_api_call from '../../lib/api/mcp/get-all-connected-mcp';
import { submitLogToBugsnag } from '../../lib/api/log';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import { useDispatch } from 'react-redux';
import { setActiveMCPIntegrations } from '../../store/reducers/chatSlice';
import { useLocation } from 'react-router-dom';
import TaraChatInfoTooltip from './TaraChatInfoTooltip';

interface TaraMCP {
  id: number;
  mcp_name: string;
  slug: string;
  logo: string;
  is_default: boolean;
}

interface ConnectedMCP {
  id: number;
  tara_mcp_id: number;
  is_active: boolean;
  oauth_required: boolean;
  is_valid: boolean;
  tara_mcp: TaraMCP;
}

interface ProcessedMCP {
  id: number;
  tara_mcp_id: number;
  mcp_name: string;
  slug: string;
  oauth_required: boolean;
  is_valid: boolean;
  logo: string;
  is_default: boolean;
  has_error: boolean;
}

const TaraMCPConnections = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // popup
  const [isTaraMCPConnectionsOpen, setIsTaraMCPConnectionsOpen] =
    useState(false);
  const [activeMCPs, setActiveMCPs] = useState<ProcessedMCP[]>([]);
  const [hasMcpError, setHasMcpError] = useState(false);

  const [loading, setLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('null');

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const shouldShowChatInfo =
    location.pathname === '/tara' ||
    location.pathname.startsWith('/tara/chat/');

  const transformMCPData = (connectedMCPs: ConnectedMCP[]): ProcessedMCP[] => {
    return connectedMCPs
      .filter((item) => item.is_active)
      .map((item) => ({
        id: item.id,
        tara_mcp_id: item.tara_mcp_id,
        mcp_name: item.tara_mcp.mcp_name,
        slug: item.tara_mcp.slug,
        oauth_required: item.oauth_required,
        is_valid: item.is_valid,
        logo: item.tara_mcp.logo,
        is_default: item.tara_mcp.is_default,
        has_error: item.is_valid === false || item.oauth_required === true,
      }));
  };

  const sortMCPs = (mcpList: ProcessedMCP[]): ProcessedMCP[] => {
    return [...mcpList].sort((a, b) => {
      // Highest priority: items with errors go to the top
      if (a.has_error !== b.has_error) {
        return a.has_error ? -1 : 1;
      }

      // If both have the same error status, then put AdUp items at the bottom
      const aIsAdUp = a.mcp_name.startsWith('AdUp');
      const bIsAdUp = b.mcp_name.startsWith('AdUp');
      if (aIsAdUp !== bIsAdUp) {
        return aIsAdUp ? 1 : -1;
      }

      // If both are AdUp or both are not AdUp, sort by ID
      return a.id - b.id;
    });
  };

  const getActiveMCPs = async () => {
    setLoading('pending');
    try {
      const connectedMCPs = await get_all_connected_mcp_api_call();

      if (!connectedMCPs || !Array.isArray(connectedMCPs)) {
        setActiveMCPs([]);
        dispatch(setActiveMCPIntegrations([]));
        setLoading('null');
        return;
      }

      const transformedMCPs = transformMCPData(connectedMCPs);

      if (transformedMCPs.length > 0) {
        const sortedMCPs = sortMCPs(transformedMCPs);
        setActiveMCPs(sortedMCPs);
        dispatch(setActiveMCPIntegrations(sortedMCPs));
        setHasMcpError(sortedMCPs.some((item) => item.has_error === true));
        setLoading('success');
      } else {
        setActiveMCPs([]);
        dispatch(setActiveMCPIntegrations([]));
        setLoading('null');
      }
    } catch (error) {
      setLoading('error');
      console.error('Error fetching data:', error);
      submitLogToBugsnag('error', `Error in fetching MCP data: ${error}`);
    }
  };

  useEffect(() => {
    getActiveMCPs();
  }, []);

  // Effect to handle click outside and close the modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsTaraMCPConnectionsOpen(false);
      }
    };

    // Attach event listener to the document
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className='taraMCPConnections-wrapper'>
      <div
        ref={dropdownRef}
        onClick={() => setIsTaraMCPConnectionsOpen(!isTaraMCPConnectionsOpen)}
        className='taraMCPConnections'
      >
        {/*-------- Desktop --------*/}
        <div
          data-is-mcp-connections-open={
            isTaraMCPConnectionsOpen ? 'true' : 'false'
          }
          data-has-mcp-error={hasMcpError ? 'true' : 'false'}
          className='taraMCPConnections--title-desktop'
        >
          {hasMcpError && <span>⚠️</span>}
          <span>{t('ConnectedIntegrations')}</span>
          <CustomSVGs svg={arrowHeadLeftSVG} />
        </div>

        {/*-------- Mobile ---------*/}
        <div
          data-is-mcp-connections-open={
            isTaraMCPConnectionsOpen ? 'true' : 'false'
          }
          className='taraMCPConnections--title-mobile'
        >
          {hasMcpError && (
            <>
              <span style={{ marginRight: '0.7rem' }}>⚠️</span>
            </>
          )}
          <CustomSVGs svg={rocketSVG} />
        </div>

        <AnimatePresence>
          {isTaraMCPConnectionsOpen && (
            <motion.div
              className='taraMCPConnections__dropdown'
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <div className='taraMCPConnections__dropdown--top'>
                <h3>{t('ConnectedPlatforms')}</h3>
                <span>{t('ManageYourDataConnections')}</span>
              </div>
              <ul className='taraMCPConnections__dropdown--list'>
                {loading === 'pending' && <LoadingSpinner />}
                {loading === 'error' && (
                  <div className='taraMCPConnections__dropdown--list-error'>
                    <span>{t('ErrorLoadingData')}</span>
                  </div>
                )}
                {loading === 'null' && (
                  <div className='taraMCPConnections__dropdown--list-null'>
                    <span>{t('NoActiveConnections')}</span>
                  </div>
                )}
                {loading === 'success' && (
                  <>
                    {activeMCPs?.map((item) => {
                      return (
                        <li
                          className='taraMCPConnections__dropdown--list-item'
                          key={item?.id}
                          onClick={() => {
                            if (item?.has_error) {
                              navigate(`/integrations?errorId=${item?.id}`);
                              setIsTaraMCPConnectionsOpen(false);
                            }
                          }}
                        >
                          <div className='taraMCPConnections__dropdown--list-item-content'>
                            <div className='taraMCPConnections__dropdown--list-item-img'>
                              <img src={item?.logo} alt={item?.mcp_name} />
                            </div>
                            <div className='taraMCPConnections__dropdown--list-item-title'>
                              {item?.mcp_name}
                            </div>
                          </div>
                          {item?.has_error ? (
                            <span className='taraMCPConnections__dropdown--list-item-error'>
                              ⚠️
                            </span>
                          ) : (
                            <div className='taraMCPConnections__dropdown--list-item-dot'></div>
                          )}
                        </li>
                      );
                    })}
                  </>
                )}
              </ul>
              <div
                onClick={() => {
                  navigate('/integrations');
                  setIsTaraMCPConnectionsOpen(false);
                }}
                className='taraMCPConnections__dropdown--bottom'
              >
                <span>{t('ManageAllConnections')}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {shouldShowChatInfo && (
        <div className='taraMCPConnections-container'>
          <TaraChatInfoTooltip />
        </div>
      )}
    </div>
  );
};

export default TaraMCPConnections;
